/**
 * ProtectedRoute - React Router route guard for authenticated users
 *
 * Redirects unauthenticated users to /login while preserving intended destination.
 * Supports both direct children and React Router Outlet pattern.
 */

import type { ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/adminAccess';
import { EmailVerificationGate } from './EmailVerificationGate';

interface ProtectedRouteProps {
  children?: ReactNode;
  requireAdmin?: boolean;
  requireEmailVerification?: boolean;
}

export const ProtectedRoute = ({ children, requireAdmin = false, requireEmailVerification = false }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const isAdmin = isAdminUser(user);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login, save intended destination
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search, hash: location.hash } }}
      />
    );
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">Admins only</div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            You need admin access to view this page. Please switch to an admin account or contact support.
          </p>
          <div className="mt-6">
            <Link className="text-indigo-600 font-medium" to="/">Return home</Link>
          </div>
        </div>
      </div>
    );
  }

  // Check email verification if required (only for email/password users, not Google OAuth)
  if (requireEmailVerification && !user.emailVerified) {
    const isEmailPasswordUser = user.providerData.some(
      (provider) => provider.providerId === 'password'
    );

    // Block only email/password users who haven't verified
    // Google OAuth users bypass this check (they're pre-verified)
    if (isEmailPasswordUser) {
      return <EmailVerificationGate />;
    }
  }

  // Render children if provided, otherwise render Outlet for nested routes
  return children ? <>{children}</> : <Outlet />;
};
